/**
 * Mirrors server NPILookupService address normalization for facility cards:
 * - Interstate wording (INTERSTATE 35 ≡ I 35)
 * - USPS directionals (SOUTH ADAMS ≡ S ADAMS) before street-type segment
 * - Trailing street-type segment like _street_line_dedup_segment (ST ≡ STREET ≡ RD …)
 * - ZIP: first five digits only (ZIP+4 and 9-digit continuous forms align)
 */
const INTERSTATE_WORD_RE = /\bINTERSTATE\s+(?:HWY\s+)?(\d{1,3})\b/gi;
const INTERSTATE_I_DASH_RE = /\bI\s*-\s*(\d{1,3})\b/gi;
const INTERSTATE_I_SPACE_RE = /\bI\s+(\d{1,3})\b/gi;

/** Whole-token USPS directionals → abbreviations (NPPES mixes spelled-out and abbreviated). */
const USPS_DIRECTIONAL_TOKEN: Record<string, string> = {
  NORTH: "N",
  SOUTH: "S",
  EAST: "E",
  WEST: "W",
  NORTHEAST: "NE",
  NORTHWEST: "NW",
  SOUTHEAST: "SE",
  SOUTHWEST: "SW",
};

const ST_STREET_RD_ROAD_EQUIV = new Set([
  "ST",
  "STREET",
  "STS",
  "RD",
  "ROAD",
  "RDS",
]);

const STREET_TYPE_SYNONYMS_TO_CANON: Record<string, string> = {
  AVE: "AVE",
  AVENUE: "AVE",
  AVES: "AVE",
  BLVD: "BLVD",
  BOULEVARD: "BLVD",
  DR: "DR",
  DRIVE: "DR",
  DRS: "DR",
  LN: "LN",
  LANE: "LN",
  LNS: "LN",
  CT: "CT",
  COURT: "CT",
  CTS: "CT",
  PL: "PL",
  PLACE: "PL",
  PLS: "PL",
  PKWY: "PKWY",
  PARKWAY: "PKWY",
  PKWYS: "PKWY",
  HWY: "HWY",
  HIGHWAY: "HWY",
  HWYS: "HWY",
  CIR: "CIR",
  CIRCLE: "CIR",
  CIRS: "CIR",
  TER: "TER",
  TERRACE: "TER",
  TERS: "TER",
  TRL: "TRL",
  TRAIL: "TRL",
  TRLS: "TRL",
};

export function canonicalizeInterstateHighwayForDedup(streetLine: string): string {
  let s = (streetLine || "").trim();
  if (!s) return s;
  s = s.replace(INTERSTATE_WORD_RE, (_m, n: string) => `I${n}`);
  s = s.replace(INTERSTATE_I_DASH_RE, (_m, n: string) => `I${n}`);
  s = s.replace(INTERSTATE_I_SPACE_RE, (_m, n: string) => `I${n}`);
  return s.toUpperCase();
}

function normalizeUspsDirectionalTokens(upperLine: string): string {
  return upperLine
    .split(/\s+/)
    .filter(Boolean)
    .map((raw) => {
      const stripped = raw.replace(/\.$/, "");
      return USPS_DIRECTIONAL_TOKEN[stripped] ?? raw;
    })
    .join(" ");
}

/**
 * Same idea as NPILookupService._street_line_dedup_segment: stable key for the
 * thoroughfare despite NPPES street-type disagreements.
 */
export function streetLineDedupSegment(streetLine: string): string {
  const s = (streetLine || "").trim().toUpperCase();
  if (!s) return "";
  const tokens = s.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return s;
  const last = tokens[tokens.length - 1].replace(/\.$/, "");
  const prefix = tokens.slice(0, -1).join(" ");
  if (ST_STREET_RD_ROAD_EQUIV.has(last)) {
    return `${prefix}|ST_RD`;
  }
  const canon = STREET_TYPE_SYNONYMS_TO_CANON[last];
  if (canon) return `${prefix}|${canon}`;
  return s;
}

function zip5(postal: string | undefined | null): string {
  if (postal == null || postal === "") return "";
  const d = String(postal).replace(/\D/g, "");
  return d.length >= 5 ? d.slice(0, 5) : d;
}

/**
 * Stable key for grouping facility cards by practice location (normalized street segment,
 * city, state, ZIP5).
 */
export function facilityAddressGroupingKey(facility: Record<string, unknown>): string {
  const primary = facility.primary_address as
    | Record<string, string | undefined>
    | undefined;
  const addr =
    primary ||
    (facility.address_1 || facility.city
      ? {
          address_1: facility.address_1 as string | undefined,
          city: facility.city as string | undefined,
          state: facility.state as string | undefined,
          postal_code: facility.postal_code as string | undefined,
        }
      : null);
  if (!addr) {
    const loc = String(facility.full_address || facility.site_name || "").trim();
    return loc.toUpperCase() || "UNKNOWN";
  }
  const city = (addr.city || "").trim().toUpperCase();
  const state = (addr.state || "").trim().toUpperCase();
  const z = zip5(addr.postal_code);
  const line1 = String(addr.address_1 || "").split(",")[0].trim();
  const interstate = canonicalizeInterstateHighwayForDedup(line1);
  const directionals = normalizeUspsDirectionalTokens(interstate);
  const street = streetLineDedupSegment(directionals);
  return `${street}|${city}|${state}|${z}`;
}
